import { cloneDeep, get, has, set } from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import React, { Component } from 'react';

import {
  BACKEND_DATE_FORMAT,
  INPUT_DATE_FORMAT,
  MINIMUM_ADULT_AGE,
  MINIMUM_DATE,
  UK_DIAL_CODE,
  DEFAULT_COUNTRY_NAME,
  cleanupCountryName,
} from '../../../config/helpers';
import REGEX from '../../../config/regex';
import { errorCodes, getErrorMessageKey } from '../../../util/errorCodes';
import jsonPost from '../../../util/jsonPost';
import { labelsPropTypes, passengerDetailsLoadPropTypes } from './PropTypes';
import { buildRoomsViewModel } from './accommodationHelpers';
import { validateCampingAllocation, formatCampingErrors } from './campingValidation';

import {
  findCurrencyItemFromListWithFallback,
  getInitialCurrencyState,
} from '../../../util/currency';
import Button from '../Atoms/Button';
import Checkbox from '../Atoms/Checkbox';
import Insurance from '../BookingFieldset/Insurance';
import LinkedBookings from '../BookingFieldset/LinkedBookings';
import MemberAddress from '../BookingFieldset/MemberAddress';
import MemberDetails from '../BookingFieldset/MemberDetails';
import RoomAllocation from '../BookingFieldset/RoomAllocation';
import {
  initFieldsetProps,
  markRequiredFields,
  preventKeyCode,
  updateField,
  validateAgeForPaxType,
  validateFieldset,
} from '../BookingFieldset/helpers';
import { getTourDatesWithStopoversConsidered } from '../BookingStopoverOptions/helpers';
import { withErrorBoundary } from '../Core/ErrorBoundary';
import ErrorMessage from '../Core/ErrorMessage';
import PackageSummary from '../PackageSummary';
import StickyComponent from '../StickyComponent';
import CUSTOM_EVENT from '../../../util/customEvents';
import Icon from '../Atoms/Icon';

const CAMPING_SUB_ITEM_TYPES = new Set(['camping', 'campsite', 'campsitePitch', 'campsiteTent']);

function normaliseToSubmissionType(accommodationType) {
  if (accommodationType === 'hotel') return 'hotel';
  if (CAMPING_SUB_ITEM_TYPES.has(accommodationType)) return 'camping';
  return null;
}

const propTypes = exact({
  apiURL: PropTypes.string.isRequired,
  data: passengerDetailsLoadPropTypes.isRequired,
  labels: labelsPropTypes,
  pcaPredictKey: PropTypes.string,
  orderId: PropTypes.string.isRequired,
});

const defaultProps = {
  labels: undefined,
  pcaPredictKey: null,
};

const requiredFields = {
  leadPartyMember: [
    'address.addressLine1',
    'address.city',
    'address.postcode',
    'dateOfBirth',
    'emailAddress',
    'fullName.forename',
    'fullName.prefix',
    'fullName.surname',
    'mobile',
  ],
  partyMember: [
    'dateOfBirth',
    'fullName.forename',
    'fullName.prefix',
    'fullName.surname',
    'insuranceCover',
  ],
};

class BookingDetails extends Component {
  constructor(props) {
    super(props);

    this.allocateRooms = this.allocateRooms.bind(this);
    this.checkIsMember = this.checkIsMember.bind(this);
    this.checkLinkedBooking = this.checkLinkedBooking.bind(this);
    this.handleAddressPopulate = this.handleAddressPopulate.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleFieldChange = this.handleFieldChange.bind(this);
    this.handleRoomAllocationChange = this.handleRoomAllocationChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.resetLinkedBookings = this.resetLinkedBookings.bind(this);
    this.toggleBookerIsMember = this.toggleBookerIsMember.bind(this);
    this.toggleLinkedBooking = this.toggleLinkedBooking.bind(this);
    this.handleResetState = this.handleResetState.bind(this);
    this.setFieldValueToEmpty = this.setFieldValueToEmpty.bind(this);
    this.handleCurrencyChange = this.handleCurrencyChange.bind(this);
    this.calculateMemberCount = this.calculateMemberCount.bind(this);
    this.handlePassengerSelect = this.handlePassengerSelect.bind(this);
    this.handleCountrySelect = this.handleCountrySelect.bind(this);

    const { data } = props;

    let leadPartyMember = data.leadBooker;

    leadPartyMember = initFieldsetProps(leadPartyMember);
    markRequiredFields(leadPartyMember, requiredFields.leadPartyMember);

    leadPartyMember.dateOfBirth.regex = REGEX.date;
    leadPartyMember.emailAddress.regex = REGEX.email;
    leadPartyMember.mobile.regex = REGEX.mobile;
    leadPartyMember.phone.regex = REGEX.phone;

    // Set united kingdom country dial code to default if empty string is given
    if (!leadPartyMember.phoneCountryDialCode.value) {
      leadPartyMember.phoneCountryDialCode.value = UK_DIAL_CODE;
      leadPartyMember.phoneCountryDialCodeName.value = DEFAULT_COUNTRY_NAME;
    }

    // Set united kingdom country dial code to default if empty string is given
    if (!leadPartyMember.mobileCountryDialCode.value) {
      leadPartyMember.mobileCountryDialCode.value = UK_DIAL_CODE;
      leadPartyMember.phoneCountryDialCodeName.value = DEFAULT_COUNTRY_NAME;
    }

    const rooms = buildRoomsViewModel(data.accommodationSubItems, data.selectedOptions);

    const presentTypes = [
      ...new Set(
        (rooms ?? [])
          .filter((r) => r.isSelected)
          .map((r) => normaliseToSubmissionType(r.subItem.accommodationType))
          .filter(Boolean)
      ),
    ];

    const partyMembers = data.partyMembers.map((partyMember) => {
      const partyMemberModified = initFieldsetProps(partyMember);
      markRequiredFields(
        partyMemberModified,
        data.chestSizeOptions.length > 0
          ? [...requiredFields.partyMember, 'chestSizeKey']
          : requiredFields.partyMember
      );

      partyMemberModified.dateOfBirth.regex = REGEX.date;

      partyMemberModified.accommodationSubItemAllocations = Object.fromEntries(
        presentTypes.map((type) => [
          type,
          {
            roomAllocation: {
              isInvalid: false,
              isRequired: true,
              regex: undefined,
              value: { key: null, index: -1 },
            },
          },
        ])
      );

      return partyMemberModified;
    });

    const currentCurrency = getInitialCurrencyState(props.data.pricePerCurrency);
    const currencyPriceSetting = findCurrencyItemFromListWithFallback(
      props.data.pricePerCurrency,
      currentCurrency.code
    );

    this.state = {
      currentCurrencyCodeAndSymbol: currentCurrency,
      currencyPriceSetting,
      errorMessage: null,
      isLoading: false,
      isBookerAMember: data.isBookerAMember,
      isLinkedBooking: false,
      leadPartyMember,
      linkedBookingName: '',
      linkedBookingReference: '',
      partyMembers,
      rooms,
      totalPrice: currencyPriceSetting.totalPrice,
    };
  }

  componentDidMount() {
    this.checkIsMember();
    this.checkLinkedBooking();
    this.allocateRooms();
    window.addEventListener('pageshow', this.handleResetState);
    window.addEventListener(CUSTOM_EVENT.CURRENCY_CHANGE, this.handleCurrencyChange);
  }

  componentWillUnmount() {
    window.removeEventListener('pageshow', this.handleResetState);
    window.removeEventListener(CUSTOM_EVENT.CURRENCY_CHANGE, this.handleCurrencyChange);
  }

  handleCurrencyChange(event) {
    const newCurrencyCode = event?.detail?.code;
    if (newCurrencyCode) {
      this.setState({ currentCurrencyCodeAndSymbol: event.detail });
    } else console.error('Currency data not found for:', newCurrencyCode);

    const newCurrencyPriceSetting = findCurrencyItemFromListWithFallback(
      this.props.data.pricePerCurrency,
      newCurrencyCode
    );
    if (newCurrencyPriceSetting)
      this.setState({
        currencyPriceSetting: newCurrencyPriceSetting,
        totalPrice: newCurrencyPriceSetting.totalPrice,
      });
    else console.error('Currency not found from current currency chosen to currency from model');
  }

  handleResetState() {
    this.setState({ isLoading: false });
  }

  allocateRooms() {
    const { partyMembers, rooms } = this.state;

    rooms?.forEach((room) => {
      const roomModified = room;
      roomModified.items = [...room.items.map(() => ({ occupancy: 0 }))];
      return roomModified;
    });

    const roomAllocations = partyMembers.flatMap((partyMember) =>
      Object.values(partyMember.accommodationSubItemAllocations ?? {}).map(
        (a) => a.roomAllocation.value
      )
    );

    roomAllocations.forEach((roomAllocation) => {
      if (!roomAllocation || roomAllocation.index < 0) return;
      const { index, key } = roomAllocation;
      const roomTypeIndex = rooms.findIndex((room) => room.key === key);
      if (roomTypeIndex < 0) return;
      rooms[roomTypeIndex].items[index].occupancy += 1;
    });

    this.setState({ rooms });
  }

  checkIsMember() {
    const { isBookerAMember, partyMembers, leadPartyMember } = this.state;

    if (isBookerAMember) {
      const fieldsToSync = ['prefix', 'forename', 'surname', 'dateOfBirth'];
      fieldsToSync.forEach((field) => {
        if (field === 'dateOfBirth') {
          leadPartyMember[field] = partyMembers[0][field];
        } else {
          leadPartyMember.fullName[field] = partyMembers[0].fullName[field];
        }
      });
    } else {
      const emptyFields = {
        fullName: {
          prefix: { ...leadPartyMember.fullName.prefix, value: '' },
          forename: { ...leadPartyMember.fullName.forename, value: '' },
          surname: { ...leadPartyMember.fullName.surname, value: '' },
        },
        dateOfBirth: { ...leadPartyMember.dateOfBirth, value: '' },
      };

      Object.assign(leadPartyMember, emptyFields);
    }

    this.setState({ partyMembers });
  }

  checkLinkedBooking() {
    const { data } = this.props;

    if (data.linkedBookingReference) {
      this.setState({
        isLinkedBooking: true,
        linkedBookingReference: data.linkedBookingReference,
        linkedBookingName: data.linkedBookingName,
      });
    }
  }

  toggleBookerIsMember(event) {
    const isBookerAMember = event.target.value === 'yes';
    const { leadPartyMember, partyMembers } = this.state;

    if (isBookerAMember) {
      const fieldsToSync = ['prefix', 'forename', 'surname', 'dateOfBirth'];
      fieldsToSync.forEach((field) => {
        if (field === 'dateOfBirth') {
          leadPartyMember[field] = partyMembers[0][field];
        } else {
          leadPartyMember.fullName[field] = partyMembers[0].fullName[field];
        }
      });
    } else {
      const emptyFields = {
        fullName: {
          prefix: { ...leadPartyMember.fullName.prefix, value: '' },
          forename: { ...leadPartyMember.fullName.forename, value: '' },
          surname: { ...leadPartyMember.fullName.surname, value: '' },
        },
        dateOfBirth: { ...leadPartyMember.dateOfBirth, value: '' },
      };

      Object.assign(leadPartyMember, emptyFields);
    }

    this.setState({ isBookerAMember, partyMembers });
  }

  toggleLinkedBooking(event) {
    const isLinkedBooking = event.target.checked;

    if (!isLinkedBooking) {
      this.resetLinkedBookings();
    }

    this.setState({
      isLinkedBooking,
    });
  }

  resetLinkedBookings() {
    this.setState({
      linkedBookingName: '',
      linkedBookingReference: '',
    });
  }

  validateForm() {
    let formIsInvalid = false;
    let invalidDateOfBirthFormat = false;
    let missingRoomAllocations = false;
    let unallocatedRoom = false;

    const { leadPartyMember } = this.state;

    // Check required fields & regex for lead party member
    const lpmIsInvalid = validateFieldset(leadPartyMember);

    if (lpmIsInvalid) formIsInvalid = true;

    // Check lead party member DOB format
    if (!moment(leadPartyMember.dateOfBirth.value, INPUT_DATE_FORMAT, true).isValid()) {
      invalidDateOfBirthFormat = true;
    }

    // Check lead party member is over 18
    let lpmIsUnderMinAge = false;
    let lpmDateOfBirthBeforeMinDate = false;

    if (!formIsInvalid) {
      // Check DOB is after 1900 and LPM is older than 18 as of the tour's return date
      const lpmDateOfBirth = moment(leadPartyMember.dateOfBirth.value, INPUT_DATE_FORMAT);
      lpmIsUnderMinAge =
        moment(this.props.data.tourToDate).diff(lpmDateOfBirth, 'years') < MINIMUM_ADULT_AGE;
      lpmDateOfBirthBeforeMinDate = moment(MINIMUM_DATE).isAfter(lpmDateOfBirth);
      leadPartyMember.dateOfBirth.isInvalid = lpmIsUnderMinAge || lpmDateOfBirthBeforeMinDate;
    }

    // Check required fields, regex, DOB & room allocations for party members
    let pmDateOfBirthBeforeMinDate = false;

    const partyMembersList = this.state.partyMembers;

    const partyMembers = partyMembersList.map((partyMember) => {
      const pm = partyMember;

      if (!moment(pm.dateOfBirth.value, INPUT_DATE_FORMAT, true).isValid()) {
        invalidDateOfBirthFormat = true;
      }

      const pmDateOfBirth = moment(pm.dateOfBirth.value, INPUT_DATE_FORMAT);
      pmDateOfBirthBeforeMinDate = moment(MINIMUM_DATE).isAfter(pmDateOfBirth);

      const pmIsInvalid = validateFieldset(pm, ({ fieldKey, field }) => {
        if (fieldKey === 'dateOfBirth') {
          const momentValue = moment(field.value, INPUT_DATE_FORMAT);
          const isInvalidDate = moment(MINIMUM_DATE).isAfter(momentValue);

          return isInvalidDate || !momentValue.isValid();
        }

        return field.isInvalid;
      });

      if (invalidDateOfBirthFormat || pmDateOfBirthBeforeMinDate) {
        pm.dateOfBirth.isInvalid = true;
      }

      if (pmIsInvalid) formIsInvalid = true;

      // If the package contains rooms
      if (this.state.rooms?.length > 0) {
        Object.values(pm.accommodationSubItemAllocations ?? {}).forEach(({ roomAllocation }) => {
          if (roomAllocation.value.index < 0) {
            missingRoomAllocations = true;
            roomAllocation.isInvalid = true; // eslint-disable-line no-param-reassign
          }
        });
      }

      return pm;
    });

    // Check for unallocated rooms
    this.state.rooms?.forEach((room) => {
      if (room.isSelected) {
        room.items.forEach((occupancy) => {
          if (occupancy.occupancy === 0) unallocatedRoom = true;
        });
      }
    });

    // Camping tent occupancy rule validation (gated by server feature flag)
    let campingErrors = [];
    if (this.props.data.enforceTentOccupancyRules) {
      const result = validateCampingAllocation(
        this.state.partyMembers,
        this.props.data.accommodationSubItems
      );
      campingErrors = result.errors;

      if (campingErrors.length > 0) {
        // Mark each party member whose tent allocation is implicated as invalid
        partyMembers.forEach((member) => {
          const pm = member;
          const campingAlloc = pm.accommodationSubItemAllocations?.camping?.roomAllocation;
          if (!campingAlloc || campingAlloc.value.index < 0) return;
          const isImplicated = campingErrors.some(
            (e) =>
              e.tentSubItemKey === campingAlloc.value.key &&
              e.instanceIndex === campingAlloc.value.index
          );
          if (isImplicated) {
            campingAlloc.isInvalid = true;
          }
        });
      }
    }

    const { labels } = this.props;

    // Build error message
    let errorMessage = null;
    if (formIsInvalid) errorMessage = labels.validationReviewFields;
    else if (invalidDateOfBirthFormat)
      errorMessage = labels.validationDateOfBirthFormat.replace(
        '[INPUT_DATE_FORMAT]',
        `${INPUT_DATE_FORMAT}`
      );
    else if (lpmIsUnderMinAge) errorMessage = labels.validationLeadMinimumAge;
    else if (lpmDateOfBirthBeforeMinDate) errorMessage = labels.validationDateOfBirthInvalid;
    else if (campingErrors.length > 0) errorMessage = formatCampingErrors(campingErrors, labels);
    // camping validations will trump the below room allocations.
    else if (missingRoomAllocations) errorMessage = labels.validationMissingRoomAllocations;
    else if (unallocatedRoom) errorMessage = labels.validationMissingRoomAllocations;

    // Update state & return if form is invalid
    this.setState({ errorMessage, leadPartyMember, partyMembers });
    return (
      invalidDateOfBirthFormat ||
      formIsInvalid ||
      lpmIsUnderMinAge ||
      lpmDateOfBirthBeforeMinDate ||
      missingRoomAllocations ||
      unallocatedRoom ||
      campingErrors.length > 0
    );
  }

  handleBlur(event) {
    const newState = updateField(this.state, event.target.name, event.target.value, ({ value }) => {
      let newValue = value;

      if (typeof value === 'string') {
        newValue = value.trim();
      }

      return newValue;
    });

    this.setState(newState);
  }

  setFieldValueToEmpty(name) {
    const newState = updateField(this.state, name, '');

    this.setState(newState);
  }

  getMutatedStateAfterCheckToSyncFieldData(name, value, newState) {
    // If name is undefined, return the state unchanged
    if (!name) {
      return newState;
    }

    // Only sync fields that are not address, contact, or chest size related
    const fieldsToSyncFromAndToLeadPartyMember = ['prefix', 'forename', 'surname', 'dateOfBirth'];

    const { selectedPassengerIndex } = this.state;
    const leadPrefix = 'leadPartyMember';
    const partyPrefix = `partyMembers[${selectedPassengerIndex || 0}]`;

    const isSyncPassengerTypeValid = name.startsWith(leadPrefix) || name.startsWith(partyPrefix);
    const isSyncFieldValid = fieldsToSyncFromAndToLeadPartyMember.some((field) =>
      name.includes(field)
    );

    if (this.state.isBookerAMember && isSyncPassengerTypeValid && isSyncFieldValid) {
      let fromPath = '';
      let toPath = '';

      if (name.startsWith(leadPrefix)) {
        fromPath = leadPrefix;
        toPath = partyPrefix;
      } else {
        fromPath = partyPrefix;
        toPath = leadPrefix;
      }

      const syncPath = name.replace(fromPath, toPath);
      return updateField(newState, syncPath, value);
    }

    return newState;
  }

  handleCountrySelect(countryName, dialCode = UK_DIAL_CODE, type = 'phone') {
    // Use 'United Kingdom' as the default country if none is provided
    const countryNameToUse = countryName || DEFAULT_COUNTRY_NAME;

    // Remove parentheses and their contents from country name if present
    const cleanCountryName = cleanupCountryName(countryNameToUse);

    // Create a new state object
    let newState = { ...this.state };

    // Field names to update based on type
    const countryNameField =
      type === 'phone'
        ? 'leadPartyMember.phoneCountryDialCodeName'
        : 'leadPartyMember.mobileCountryDialCodeName';

    const dialCodeField =
      type === 'phone'
        ? 'leadPartyMember.phoneCountryDialCode'
        : 'leadPartyMember.mobileCountryDialCode';

    // Update the country name field
    newState = updateField(newState, countryNameField, cleanCountryName);

    // Update the dial code field
    newState = updateField(newState, dialCodeField, dialCode);

    // Apply the same field syncing logic as handleFieldChange
    newState = this.getMutatedStateAfterCheckToSyncFieldData(
      countryNameField,
      cleanCountryName,
      newState
    );
    newState = this.getMutatedStateAfterCheckToSyncFieldData(dialCodeField, dialCode, newState);

    // Update the state
    this.setState(newState);
  }

  handleFieldChange(event) {
    const { name, value } = event.target;

    // Handle date formatting
    let formattedValue = value;
    if (name?.endsWith('dateOfBirth')) {
      const currentValue = get(this.state, `${name}.value`, '');
      if (value.length > currentValue.length) {
        if (value.length === 2 || value.length === 5) {
          formattedValue += '/';
        }
      }
    }

    // New Updated State For Named Field
    let newState = updateField(this.state, name, formattedValue);

    // If we are syncing to or from lead party member, mutate state
    newState = this.getMutatedStateAfterCheckToSyncFieldData(name, formattedValue, newState);

    this.setState(newState);
  }

  handleRoomAllocationChange(event) {
    const { name } = event.target;
    let { value } = event.target;

    const stateClone = cloneDeep(this.state);

    if (!has(stateClone, name)) return;

    const [key, indexString] = value.split(',');
    const index = parseInt(indexString, 10);

    const currentValue = get(stateClone, `${name}.value`);

    value = { ...currentValue, key, index };

    set(stateClone, `${name}.value`, value);

    this.setState(stateClone, () => {
      this.allocateRooms();
    });
  }

  handleAddressPopulate(address) {
    const { leadPartyMember } = this.state;

    leadPartyMember.address = address;

    this.setState({ leadPartyMember });
  }

  calculateMemberCount(currentPaxKey, currentIndex) {
    // Count how many members with the same paxKey appear before this index
    return (
      this.state.partyMembers
        .slice(0, currentIndex)
        .filter((member) => member.paxKey.value === currentPaxKey).length + 1
    );
  }

  handleSubmit(event) {
    event.preventDefault();

    const formIsInvalid = this.validateForm();

    // Validate ages for all party members
    const ageValidationErrors = this.state.partyMembers
      .map((member, index) => {
        if (!member.dateOfBirth?.value) return null;

        const validation = validateAgeForPaxType(
          member.dateOfBirth.value,
          this.props.data.paxTypes,
          member.paxKey,
          this.props.labels.validationPaxDateOfBirthInvalid,
          false,
          false,
          MINIMUM_ADULT_AGE,
          this.props.data.tourToDate
        );

        if (!validation.isValid) {
          return {
            index,
            message: validation.message,
          };
        }
        return null;
      })
      .filter(Boolean);

    if (ageValidationErrors.length > 0) {
      // Update state with validation errors
      const newPartyMembers = [...this.state.partyMembers];
      let errorMessage = '';
      ageValidationErrors.forEach((error) => {
        errorMessage += `${error.message}<br/>`;
        newPartyMembers[error.index] = {
          ...newPartyMembers[error.index],
          dateOfBirth: {
            ...newPartyMembers[error.index].dateOfBirth,
            isInvalid: true,
            validationMessage: error.message,
          },
        };
      });

      this.setState({
        partyMembers: newPartyMembers,
        errorMessage,
      });
      return;
    }

    if (formIsInvalid) return;

    this.setState({
      errorMessage: null,
      isLoading: true,
    });

    const {
      isBookerAMember,
      isLinkedBooking,
      leadPartyMember,
      linkedBookingName,
      linkedBookingReference,
      partyMembers,
    } = this.state;

    const leadPartyMemberObj = {
      address: {
        addressLine1: leadPartyMember.address.addressLine1.value,
        addressLine2: leadPartyMember.address.addressLine2.value,
        city: leadPartyMember.address.city.value,
        county: leadPartyMember.address.county.value,
        postcode: leadPartyMember.address.postcode.value,
      },
      dateOfBirth: moment(leadPartyMember.dateOfBirth.value, INPUT_DATE_FORMAT).format(
        BACKEND_DATE_FORMAT
      ),
      emailAddress: leadPartyMember.emailAddress.value,
      fullName: {
        forename: leadPartyMember.fullName.forename.value,
        prefix: leadPartyMember.fullName.prefix.value,
        surname: leadPartyMember.fullName.surname.value,
      },
      mobile: leadPartyMember.mobile.value,
      mobileCountryDialCode: leadPartyMember.mobileCountryDialCode.value,
      mobileCountryDialCodeName:
        leadPartyMember.mobileCountryDialCodeName.value || DEFAULT_COUNTRY_NAME,
      phone: leadPartyMember.phone.value,
      phoneCountryDialCode: leadPartyMember.phoneCountryDialCode.value,
      phoneCountryDialCodeName:
        leadPartyMember.phoneCountryDialCodeName.value || DEFAULT_COUNTRY_NAME,
    };

    const partyMembersList = partyMembers.map((partyMember) => ({
      dateOfBirth: moment(partyMember.dateOfBirth.value, INPUT_DATE_FORMAT).format(
        BACKEND_DATE_FORMAT
      ),
      fullName: {
        forename: partyMember.fullName.forename.value,
        prefix: partyMember.fullName.prefix.value,
        surname: partyMember.fullName.surname.value,
      },
      insuranceCover: partyMember.insuranceCover.value,
      otherRequirements: partyMember.otherRequirements.value,
      accommodationSubItemAllocations: Object.entries(
        partyMember.accommodationSubItemAllocations ?? {}
      )
        .filter(
          ([, { roomAllocation }]) =>
            roomAllocation.value.key !== null && roomAllocation.value.index >= 0
        )
        .map(([type, { roomAllocation }]) => ({
          accommodationType: type,
          key: roomAllocation.value.key,
          index: roomAllocation.value.index,
        })),
      ...(this.props.data.chestSizeOptions.length > 0 && {
        chestSizeKey: partyMember.chestSizeKey.value,
      }),
      paxKey: partyMember.paxKey.value,
    }));

    const { orderId } = this.props;
    const options = {
      isBookerAMember,
      isLinkedBooking,
      leadBooker: leadPartyMemberObj,
      linkedBookingName,
      linkedBookingReference,
      partyMembers: partyMembersList,
      orderId,
      currency: this.state.currentCurrencyCodeAndSymbol.code,
    };

    jsonPost(this.props.apiURL, options, (response) => {
      if (response.errorCode || response.errors) {
        if (response.errorCode === errorCodes.TourBookingTourHasReachedPaxCapLimit) {
          window.location.href = response.redirectUri;
        }
        const errorMessageKey = getErrorMessageKey(response.errorCode);

        return this.setState({
          errorMessage:
            this.props.labels[errorMessageKey] || response.message || 'An error occurred',
          isLoading: false,
        });
      }

      window.location.href = response.redirectUri;
      return this.setState({ errorMessage: null });
    });
  }

  handlePassengerSelect(selectedPassenger) {
    this.setState((state) => ({
      leadPartyMember: {
        ...state.leadPartyMember,
        fullName: {
          prefix: {
            ...state.leadPartyMember.fullName.prefix,
            value: selectedPassenger.fullName.prefix,
          },
          forename: {
            ...state.leadPartyMember.fullName.forename,
            value: selectedPassenger.fullName.forename,
          },
          surname: {
            ...state.leadPartyMember.fullName.surname,
            value: selectedPassenger.fullName.surname,
          },
        },
        dateOfBirth: { ...state.leadPartyMember.dateOfBirth, value: selectedPassenger.dateOfBirth },
      },
      selectedPassengerIndex: selectedPassenger.selectedPassengerIndex,
    }));
  }

  render() {
    const { data, labels } = this.props;
    const {
      errorMessage,
      isLoading,
      isBookerAMember,
      isLinkedBooking,
      linkedBookingName,
      linkedBookingReference,
      partyMembers,
      rooms,
      leadPartyMember,
    } = this.state;

    // Get list of adult party members (excluding lead booker)
    const adultPassengers = partyMembers
      .filter((member) => {
        // Find the pax type for this member
        const paxType = data.paxTypes.find((pt) => pt.key === member.paxKey.value);

        const memberDateOfBirth = moment(member.dateOfBirth.value, INPUT_DATE_FORMAT);

        // Age is checked against the tour's return date, so members who age into a
        // different band between booking and travel aren't excluded from the dropdown
        const ageAtTour = moment(data.tourToDate).diff(memberDateOfBirth, 'years');
        const isAbovePaxTypeMinAge = ageAtTour >= paxType?.minimumAge;
        const isAboveSiteMinBookingAge = ageAtTour >= labels.siteMinimumBookingAge;

        // Check if the member is above booking age and above min pax type
        return isAbovePaxTypeMinAge && isAboveSiteMinBookingAge;
      })
      .map((member, index) => ({
        id: `member-${index}`,
        selectedPassengerIndex: index,
        fullName: {
          prefix: member.fullName.prefix.value,
          forename: member.fullName.forename.value,
          surname: member.fullName.surname.value,
        },
        dateOfBirth: member.dateOfBirth.value,
        paxAgeRange: {
          minimumAge: data.paxTypes.find((pax) => pax.key === member.paxKey.value)?.minimumAge,
          maximumAge: data.paxTypes.find((pax) => pax.key === member.paxKey.value)?.maximumAge,
        },
      }));

    const showAtolLabel = data.selectedOptions.flights.length > 0;

    const { updatedTourFromDate, updatedTourToDate } = getTourDatesWithStopoversConsidered(
      data,
      data.selectedOptions.stopovers
    );

    return (
      <section className="Booking">
        <form noValidate autoComplete="false" onSubmit={this.handleSubmit}>
          {/* Package summary */}
          <div className="Booking_group Booking_group-inset">
            <div className="Booking_content">
              <StickyComponent contained>
                <PackageSummary
                  atolProtectedText={labels.atolProtectedText}
                  currencySymbol={this.state.currentCurrencyCodeAndSymbol.symbol}
                  durationText={labels.durationText}
                  fromDate={updatedTourFromDate}
                  toDate={updatedTourToDate}
                  description={data.tourDescription}
                  preferredCardImage={labels.preferredCardImage}
                  price={this.state.totalPrice}
                  showAtolLabel={showAtolLabel}
                  title={data.tourName}
                  totalPriceText={labels.totalPriceText}
                />
              </StickyComponent>
            </div>
          </div>

          {/* party members */}
          {partyMembers.map((member, memberIndex) => {
            const memberCount = this.calculateMemberCount(member.paxKey.value, memberIndex);
            const paxInfo = data.paxTypes.find((pt) => pt.key === member.paxKey.value);

            return (
              <div
                className="Booking_group Booking_group-inset"
                // eslint-disable-next-line react/no-array-index-key
                key={`${member.paxKey.value}-${memberIndex}`}
              >
                <div className="Booking_content">
                  <fieldset className="Booking_item">
                    <div className="Booking_heading">
                      <h3 className="Booking_itemTitle">
                        {`${paxInfo?.name || member.paxKey.value} ${memberCount}`}{' '}
                        <small>({paxInfo?.description ?? ''})</small>
                      </h3>
                      <h4 className="Booking_itemSubTitle">
                        {labels.partyMemberFieldsetSubHeaderText}
                      </h4>
                    </div>
                    <MemberDetails
                      paxKey={member.paxKey}
                      paxTypes={data.paxTypes}
                      fieldNamePrefix={`partyMembers[${memberIndex}]`}
                      fieldset={partyMembers[memberIndex]}
                      handleBlur={this.handleBlur}
                      handleDOBChange={this.handleFieldChange}
                      handleFieldChange={this.handleFieldChange}
                      handleKeyDown={preventKeyCode(191)}
                      chestSizeGuideLink={labels.chestSizeGuideLink}
                      chestSizeOptions={data.chestSizeOptions}
                      chestSizePlaceholderOptionText={labels.chestSizePlaceholderOptionText}
                      showExtras
                      titleOptions={labels.titleOptions}
                      specialRequirementsLabelText={labels.specialRequirementsLabelText}
                      specialRequirementsPlaceholderText={labels.specialRequirementsPlaceholderText}
                      setFieldValueToEmpty={this.setFieldValueToEmpty}
                      titlePlaceholderText={labels.placeholderTexts.titlePlaceholderText}
                      forenamePlaceholderText={labels.placeholderTexts.forenamePlaceholderText}
                      surnamePlaceholderText={labels.placeholderTexts.surnamePlaceholderText}
                      dateOfBirthPlaceholderText={
                        labels.placeholderTexts.dateOfBirthPlaceholderText
                      }
                      validationPaxDateOfBirthInvalid={labels.validationPaxDateOfBirthInvalid}
                      validationLeadBookerMinimumBookingAgeNotValid={
                        labels.validationLeadBookerMinimumBookingAgeNotValid
                      }
                      tourToDate={data.tourToDate}
                      onPassengerSelect={(passenger) => this.handlePassengerSelect(passenger)}
                    />
                  </fieldset>
                  {rooms?.length > 0 &&
                    Object.entries(
                      partyMembers[memberIndex].accommodationSubItemAllocations ?? {}
                    ).map(([type, typeAlloc]) => {
                      const groupRooms = rooms.filter(
                        (r) =>
                          normaliseToSubmissionType(r.subItem.accommodationType) === type &&
                          r.isSelected
                      );

                      if (!groupRooms.length) return null;
                      return (
                        <fieldset key={type} className="Booking_item">
                          <div className="Booking_heading">
                            <h4 className="Booking_itemTitle Booking_itemTitle-small">
                              {type === 'hotel'
                                ? labels.roomAllocationFieldsetTitle ?? 'Room Allocation'
                                : labels.campingAllocationFieldsetTitle ?? 'Tent Allocation'}
                            </h4>
                          </div>
                          <RoomAllocation
                            fieldNamePrefix={`partyMembers[${memberIndex}].accommodationSubItemAllocations.${type}`}
                            handleFieldChange={this.handleRoomAllocationChange}
                            isInvalid={typeAlloc.roomAllocation.isInvalid}
                            rooms={groupRooms}
                            selectedValue={typeAlloc.roomAllocation.value}
                          />
                        </fieldset>
                      );
                    })}
                </div>
              </div>
            );
          })}

          {/* Lead party member */}
          <div className="Booking_group Booking_group-inset">
            <div className="Booking_content">
              <fieldset className="Booking_item">
                <div className="Booking_heading">
                  <div className="Booking_headingContent">
                    <h3 className="Booking_itemTitle">{labels.leadPartyMemberFieldsetTitle}</h3>
                    <button
                      type="button"
                      className="Booking_headingTooltip"
                      onClick={() =>
                        // eslint-disable-next-line react/no-access-state-in-setstate
                        this.setState({ leadBookerToolTip: !this.state.leadBookerToolTip })
                      }
                    >
                      <Icon id="tooltip" /> {labels.leadBookerToolTipText}
                    </button>
                    <div
                      className={`Booking_headingTooltip_leadBookerToolTipDescriptionText${
                        this.state.leadBookerToolTip ? '_visible' : ''
                      }`}
                    >
                      {labels.leadBookerToolTipDescriptionText}
                    </div>
                  </div>
                  <h4 className="Booking_itemSubTitle">
                    {labels.partyMemberFieldsetSubHeaderText}
                  </h4>
                </div>

                <div className="Booking_bookerAMember">
                  {labels.isBookerAMemberLabelText} *
                  <Checkbox
                    fieldID="isBookerMemberYes"
                    fieldName="isBookerMemberName"
                    fieldValue="yes"
                    handleChange={this.toggleBookerIsMember}
                    inputType="radio"
                    isChecked={isBookerAMember}
                    isDisabled={false}
                    modifier="redFilled"
                    text={labels.isBookerAMemberYesOptionText}
                  />
                  <Checkbox
                    fieldID="isBookerMemberNo"
                    fieldName="isBookerMemberName"
                    fieldValue="no"
                    handleChange={this.toggleBookerIsMember}
                    inputType="radio"
                    isChecked={!isBookerAMember}
                    isDisabled={false}
                    modifier="redFilled"
                    text={labels.isBookerAMemberNoOptionText}
                  />
                </div>
                {/* Lead party member details */}
                <MemberDetails
                  isBookerAMember={isBookerAMember}
                  siteMinimumBookingAge={Number(labels.siteMinimumBookingAge)}
                  tourToDate={data.tourToDate}
                  fieldNamePrefix="leadPartyMember"
                  adultPassengers={adultPassengers}
                  fieldset={leadPartyMember}
                  selectedPassengerValue={`member-${adultPassengers.findIndex(
                    (p) =>
                      p.fullName.forename === leadPartyMember.fullName.forename.value &&
                      p.fullName.surname === leadPartyMember.fullName.surname.value
                  )}`}
                  handleBlur={this.handleBlur}
                  handleDOBChange={this.handleFieldChange}
                  handleFieldChange={this.handleFieldChange}
                  handleKeyDown={preventKeyCode(191)} // Prevent user from pressing forward slash "/"
                  titleOptions={labels.titleOptions}
                  titlePlaceholderText={labels.placeholderTexts.titlePlaceholderText}
                  forenamePlaceholderText={labels.placeholderTexts.forenamePlaceholderText}
                  surnamePlaceholderText={labels.placeholderTexts.surnamePlaceholderText}
                  dateOfBirthPlaceholderText={labels.placeholderTexts.dateOfBirthPlaceholderText}
                  validationPaxDateOfBirthInvalid={labels.validationPaxDateOfBirthInvalid}
                  validationLeadBookerMinimumBookingAgeNotValid={
                    labels.validationLeadBookerMinimumBookingAgeNotValid
                  }
                  onPassengerSelect={(passenger) => this.handlePassengerSelect(passenger)}
                  leadBookerSelectorDefaultOptionText={labels.leadBookerSelectorDefaultOptionText}
                  leadBookerSelectorLabelText={labels.leadBookerSelectorLabelText}
                />
              </fieldset>
              <fieldset className="Booking_item">
                <div className="Booking_heading">
                  <h4 className="Booking_itemTitle Booking_itemTitle-small">
                    {labels.addressFieldsetTitle}
                  </h4>
                </div>
                <MemberAddress
                  apiKey={this.props.pcaPredictKey}
                  buttonText={labels.findAddressButtonText}
                  fieldNamePrefix="leadPartyMember"
                  fieldset={leadPartyMember}
                  handleAddressPopulate={this.handleAddressPopulate}
                  handleFieldChange={this.handleFieldChange}
                  excludeDialCodes={labels.excludeDialCodes}
                  dialCodes={labels.dialCodes}
                  postcodePlaceholderText={labels.placeholderTexts.postcodePlaceholderText}
                  addressLine1PlaceholderText={labels.placeholderTexts.addressLine1PlaceholderText}
                  addressLine2PlaceholderText={labels.placeholderTexts.addressLine2PlaceholderText}
                  cityPlaceholderText={labels.placeholderTexts.cityPlaceholderText}
                  telephonePlaceholderText={labels.placeholderTexts.telephonePlaceholderText}
                  mobilePhonePlaceholderText={labels.placeholderTexts.mobilePhonePlaceholderText}
                  emailAddressPlaceholderText={labels.placeholderTexts.emailAddressPlaceholderText}
                  handleCountrySelect={this.handleCountrySelect}
                />
              </fieldset>
            </div>
          </div>

          {/* Linked Bookings */}
          <div className="Booking_group Booking_group-inset">
            <div className="Booking_content">
              <LinkedBookings
                handleChange={this.toggleLinkedBooking}
                handleFieldChange={(event) => this.handleFieldChange(event, false, true)}
                isLinkedBooking={isLinkedBooking}
                linkedBookingHeadingText={labels.linkedBookingHeadingText}
                linkedBookingLabelText={labels.linkedBookingLabelText}
                linkedBookingName={linkedBookingName}
                linkedBookingNameLabelText={labels.linkedBookingNameLabelText}
                linkedBookingNamePlaceholderText={labels.linkedBookingNamePlaceholderText}
                linkedBookingReference={linkedBookingReference}
                linkedBookingReferenceLabelText={labels.linkedBookingReferenceLabelText}
                linkedBookingReferencePlaceholderText={labels.linkedBookingReferencePlaceholderText}
              />
            </div>
          </div>
          {/* Insurance */}
          {data.insuranceText && data.insuranceText !== '' && (
            <div className="Booking_group Booking_group-inset">
              <div className="Booking_content">
                <Insurance
                  handleFieldChange={this.handleFieldChange}
                  notRequiredOptionText={labels.insuranceNotRequiredOptionText}
                  partyMembers={partyMembers}
                  requiredOptionText={labels.insuranceRequiredOptionText}
                  text={data.insuranceText}
                  title={labels.insuranceFieldsetTitle}
                />
              </div>
            </div>
          )}
          {/* Submit */}
          <div className="Booking_group Booking_group-inset">
            <div className="Booking_content">
              {errorMessage && (
                <div className="Booking_item">
                  <ErrorMessage text={errorMessage} />
                </div>
              )}
              <div className="Booking_item Booking_item-center">
                <Button
                  buttonType="submit"
                  handleClick={this.handleSubmit}
                  isLoading={isLoading}
                  modifier="fill"
                  text={labels.submitButtonText}
                />
              </div>
            </div>
          </div>
        </form>
      </section>
    );
  }
}

BookingDetails.propTypes = propTypes;
BookingDetails.defaultProps = defaultProps;

export default withErrorBoundary(BookingDetails);
